import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

export default function LegalPage({ type }) {
  const { language } = useLanguage();
  const isImpressum = type === 'impressum';

  return (
    <div className="py-20 md:py-32 blueprint-bg" data-testid={`legal-page-${type}`}>
      <SEO />
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-8" data-testid={`legal-title-${type}`}>
          {isImpressum
            ? (language === 'en' ? 'Legal Notice' : language === 'es' ? 'Aviso Legal' : 'Impressum')
            : (language === 'en' ? 'Privacy Policy' : language === 'es' ? 'Política de Privacidad' : 'Datenschutzerklärung')}
        </h1>
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8 md:p-12 prose prose-slate max-w-none">
          {isImpressum ? <ImpressumContent language={language} /> : <DatenschutzContent language={language} />}
        </div>
      </div>
    </div>
  );
}

function ImpressumContent({ language }) {
  // Legally binding text in German per § 5 TMG. EN/ES are translations for reference.
  if (language === 'en') {
    return (
      <div>
        <p className="text-xs text-slate-500 italic mb-4">The legally binding version is the German one. Translation provided for convenience.</p>
        <h2>Information according to § 5 TMG</h2>
        <p>ITCS Industrial Trade &amp; Construction Systems GmbH<br />Zur Neuen Brücke 4<br />01109 Dresden<br />Germany</p>
        <h3>Contact</h3>
        <p>Phone: +49 172 8116925<br />E-Mail: info@itcs-global.com</p>
        <h3>Managing Director</h3>
        <p>Colin Scholz</p>
        <h3>Commercial Register</h3>
        <p>Registration court: Amtsgericht Dresden – HRB 47893</p>
        <h3>VAT Identification Number</h3>
        <p>DE461747790</p>
        <h3>EU Dispute Resolution</h3>
        <p>We are neither obliged nor willing to participate in dispute resolution proceedings before a consumer arbitration board.</p>
      </div>
    );
  }
  if (language === 'es') {
    return (
      <div>
        <p className="text-xs text-slate-500 italic mb-4">La versión legalmente vinculante es la alemana. Esta es una traducción de cortesía.</p>
        <h2>Información según § 5 TMG</h2>
        <p>ITCS Industrial Trade &amp; Construction Systems GmbH<br />Zur Neuen Brücke 4<br />01109 Dresden<br />Alemania</p>
        <h3>Contacto</h3>
        <p>Teléfono: +49 172 8116925<br />E-Mail: info@itcs-global.com</p>
        <h3>Director General</h3>
        <p>Colin Scholz</p>
        <h3>Registro Mercantil</h3>
        <p>Tribunal de registro: Amtsgericht Dresden – HRB 47893</p>
        <h3>Número de Identificación Fiscal (IVA)</h3>
        <p>DE461747790</p>
        <h3>Resolución de litigios de la UE</h3>
        <p>No estamos obligados ni dispuestos a participar en procedimientos de resolución de litigios ante un órgano arbitral de consumo.</p>
      </div>
    );
  }
  return (
    <div>
      <h2>Angaben gemäß § 5 TMG</h2>
      <p>ITCS Industrial Trade &amp; Construction Systems GmbH<br />Zur Neuen Brücke 4<br />01109 Dresden</p>
      <h3>Kontakt</h3>
      <p>E-Mail: info@itcs-global.com<br />Telefon: +49 172 8116925</p>
      <h3>Geschäftsführer</h3>
      <p>Colin Scholz</p>
      <h3>Registergericht</h3>
      <p>Amtsgericht Dresden – HRB 47893</p>
      <h3>Umsatzsteuer-Identifikationsnummer</h3>
      <p>DE461747790</p>
      <h3>Streitbeilegung</h3>
      <p>Wir sind zur Teilnahme an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle nicht verpflichtet und bereit.</p>
    </div>
  );
}

function DatenschutzContent({ language }) {
  if (language === 'en') {
    return (
      <div>
        <p className="text-xs text-slate-500 italic mb-4">The legally binding version is the German one. Translation provided for convenience.</p>
        <h2>Privacy Policy</h2>
        <h3>1. Data Controller</h3>
        <p>ITCS Industrial Trade &amp; Construction Systems GmbH<br />Zur Neuen Brücke 4, 01109 Dresden, Germany</p>
        <p>Phone: +49 172 8116925<br />E-Mail: info@itcs-global.com</p>
        <h3>2. Collection and Processing of Personal Data</h3>
        <p>We collect personal data only to the extent necessary for providing our website and services. Data is not shared with third parties unless required for contract fulfilment or by law.</p>
        <h3>3. Contact Form</h3>
        <p>When you contact us via the contact form, the data you provide (name, e-mail address, phone number, message) is stored and processed to handle your inquiry. The data will not be shared with third parties without your consent.</p>
        <p>Legal basis: Art. 6 (1) lit. b and f GDPR.</p>
        <h3>4. Cookies</h3>
        <p>Our website only uses technically necessary cookies required for the website's operation. These cookies do not store any personal data and do not require consent. A cookie banner is not required.</p>
        <h3>5. Hosting</h3>
        <p>Our website is hosted by Railway (Railway Corp., 340 Pine Street, Suite 1070, San Francisco, CA 94104, USA). When you visit our website, connection data is automatically stored in the server log file. This data is technically necessary and is automatically deleted after 7 days.</p>
        <p>Legal basis: Art. 6 (1) lit. f GDPR.</p>
        <h3>6. Google Maps</h3>
        <p>On our contact page and on some of our service area pages you can display a map from Google Maps (Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland). The map is only loaded after you click "Load map". Only then is data such as your IP address transmitted to Google, possibly also to the USA. We store your choice locally in your browser (localStorage); you can revoke it at any time by deleting the site data in your browser. More information: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a>.</p>
        <p>Legal basis: Art. 6 (1) lit. a GDPR (consent).</p>
        <h3>7. Your Rights</h3>
        <p>You have the right at any time to:</p>
        <ul>
          <li>Information (Art. 15 GDPR)</li>
          <li>Rectification (Art. 16 GDPR)</li>
          <li>Erasure (Art. 17 GDPR)</li>
          <li>Restriction of processing (Art. 18 GDPR)</li>
          <li>Data portability (Art. 20 GDPR)</li>
          <li>Object to processing (Art. 21 GDPR)</li>
        </ul>
        <p>To exercise your rights, please contact: info@itcs-global.com</p>
        <h3>8. Right to Complain</h3>
        <p>Saxon Data Protection Commissioner<br />Devrientstraße 5, 01067 Dresden<br />www.saechsdsb.de</p>
        <h3>9. Status</h3>
        <p>This privacy policy is dated May 2026.</p>
      </div>
    );
  }
  if (language === 'es') {
    return (
      <div>
        <p className="text-xs text-slate-500 italic mb-4">La versión legalmente vinculante es la alemana. Esta es una traducción de cortesía.</p>
        <h2>Política de Privacidad</h2>
        <h3>1. Responsable</h3>
        <p>ITCS Industrial Trade &amp; Construction Systems GmbH<br />Zur Neuen Brücke 4, 01109 Dresden, Alemania</p>
        <p>Teléfono: +49 172 8116925<br />E-Mail: info@itcs-global.com</p>
        <h3>2. Recogida y tratamiento de datos personales</h3>
        <p>Solo recogemos datos personales en la medida necesaria para prestar nuestro sitio web y servicios. No se transmiten a terceros, salvo para el cumplimiento del contrato o cuando lo exija la ley.</p>
        <h3>3. Formulario de contacto</h3>
        <p>Cuando nos contacta a través del formulario, los datos facilitados (nombre, correo electrónico, teléfono, mensaje) se almacenan y procesan para gestionar su consulta. No se compartirán con terceros sin su consentimiento.</p>
        <p>Base jurídica: Art. 6 ap. 1 lit. b y f RGPD.</p>
        <h3>4. Cookies</h3>
        <p>Nuestro sitio web utiliza exclusivamente cookies técnicamente necesarias para su funcionamiento. Estas cookies no almacenan datos personales y no requieren consentimiento. No es necesario un banner de cookies.</p>
        <h3>5. Alojamiento</h3>
        <p>Nuestro sitio web está alojado en Railway (Railway Corp., 340 Pine Street, Suite 1070, San Francisco, CA 94104, EE. UU.). Al acceder a nuestro sitio, los datos de conexión se almacenan automáticamente en el archivo de registro del servidor. Estos datos son técnicamente necesarios y se eliminan automáticamente tras 7 días.</p>
        <p>Base jurídica: Art. 6 ap. 1 lit. f RGPD.</p>
        <h3>6. Google Maps</h3>
        <p>En nuestra página de contacto y en algunas páginas de nuestras zonas de servicio puede mostrar un mapa de Google Maps (Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irlanda). El mapa solo se carga después de hacer clic en "Cargar mapa". Solo entonces se transmiten a Google datos como su dirección IP, posiblemente también a EE. UU. Guardamos su decisión localmente en su navegador (localStorage); puede revocarla en cualquier momento eliminando los datos del sitio en su navegador. Más información: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a>.</p>
        <p>Base jurídica: Art. 6 ap. 1 lit. a RGPD (consentimiento).</p>
        <h3>7. Sus derechos</h3>
        <p>Tiene derecho en todo momento a:</p>
        <ul>
          <li>Información (Art. 15 RGPD)</li>
          <li>Rectificación (Art. 16 RGPD)</li>
          <li>Supresión (Art. 17 RGPD)</li>
          <li>Limitación del tratamiento (Art. 18 RGPD)</li>
          <li>Portabilidad de los datos (Art. 20 RGPD)</li>
          <li>Oposición al tratamiento (Art. 21 RGPD)</li>
        </ul>
        <p>Para ejercer sus derechos, contacte con: info@itcs-global.com</p>
        <h3>8. Derecho de reclamación</h3>
        <p>Comisionado Sajón de Protección de Datos<br />Devrientstraße 5, 01067 Dresden<br />www.saechsdsb.de</p>
        <h3>9. Vigencia</h3>
        <p>Esta política de privacidad tiene fecha de mayo de 2026.</p>
      </div>
    );
  }
  return (
    <div>
      <h2>Datenschutzerklärung</h2>
      <h3>1. Verantwortlicher</h3>
      <p>ITCS Industrial Trade &amp; Construction Systems GmbH<br />Zur Neuen Brücke 4, 01109 Dresden, Deutschland</p>
      <p>Telefon: +49 172 8116925<br />E-Mail: info@itcs-global.com</p>
      <h3>2. Erhebung und Verarbeitung personenbezogener Daten</h3>
      <p>Wir erheben personenbezogene Daten nur, soweit dies für die Bereitstellung unserer Website und unserer Leistungen erforderlich ist. Eine Weitergabe an Dritte erfolgt nicht, es sei denn zur Vertragserfüllung oder gesetzlich vorgeschrieben.</p>
      <h3>3. Kontaktformular</h3>
      <p>Wenn Sie uns über das Kontaktformular auf unserer Website kontaktieren, werden die von Ihnen angegebenen Daten (Name, E-Mail-Adresse, Telefonnummer, Nachricht) zur Bearbeitung Ihrer Anfrage gespeichert und verarbeitet. Die Daten werden nicht ohne Ihre Einwilligung an Dritte weitergegeben.</p>
      <p><em>Rechtsgrundlage: Art. 6 Abs. 1 lit. b und f DSGVO.</em></p>
      <h3>4. Cookies</h3>
      <p>Unsere Website verwendet ausschließlich technisch notwendige Cookies, die für den Betrieb der Website erforderlich sind. Diese Cookies speichern keine personenbezogenen Daten und erfordern keine Einwilligung. Ein Cookie-Banner ist nicht erforderlich.</p>
      <h3>5. Hosting</h3>
      <p>Unsere Website wird bei Railway (Railway Corp., 340 Pine Street, Suite 1070, San Francisco, CA 94104, USA) gehostet. Beim Aufruf unserer Website werden automatisch Verbindungsdaten im Server-Logfile gespeichert. Diese Daten sind technisch notwendig und werden nach 7 Tagen automatisch gelöscht.</p>
      <p><em>Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO.</em></p>
      <h3>6. Google Maps</h3>
      <p>Auf unserer Kontaktseite und auf einzelnen Seiten zu unseren Einsatzgebieten können Sie eine Karte von Google Maps (Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland) einblenden. Die Karte wird erst geladen, wenn Sie auf „Karte laden“ klicken. Erst dann werden Daten wie Ihre IP-Adresse an Google übertragen, ggf. auch in die USA. Ihre Entscheidung speichern wir lokal in Ihrem Browser (localStorage); Sie können sie jederzeit widerrufen, indem Sie die Website-Daten in Ihrem Browser löschen. Weitere Informationen: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a>.</p>
      <p><em>Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung).</em></p>
      <h3>7. Ihre Rechte</h3>
      <p>Sie haben jederzeit das Recht auf:</p>
      <ul>
        <li>Auskunft (Art. 15 DSGVO)</li>
        <li>Berichtigung (Art. 16 DSGVO)</li>
        <li>Löschung (Art. 17 DSGVO)</li>
        <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
        <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
        <li>Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)</li>
      </ul>
      <p>Zur Ausübung Ihrer Rechte wenden Sie sich bitte an: info@itcs-global.com</p>
      <h3>8. Beschwerderecht</h3>
      <p>Sächsischer Datenschutzbeauftragter<br />Devrientstraße 5, 01067 Dresden<br />www.saechsdsb.de</p>
      <h3>9. Aktualität</h3>
      <p>Diese Datenschutzerklärung hat den Stand Mai 2026.</p>
    </div>
  );
}
